import { getLibraryItemsForSync, getMe } from '../../api/abs';
import { progressStore, setProgressFromUser } from '../../api/progressStore';
import { finishedBookIds, mediaProgressOf } from '../../api/progressLists';
import { getSession } from '../../api/session';
import type { LibraryItem, MediaProgress } from '../../api/types';
import { downloadsStore } from '../../downloads/manager';
import { createStore, storage, useStore } from '../../lib/store';
import { bookFromLibraryItem, downloadedIds, type BookSummary } from '../models';
import { libraryScopeKey } from '../scope';
import { loadScope, replaceScopeBooks, upsertScopeBooks } from './cache';

// Books sync from ABS into the local library cache: Flutter's incremental
// algorithm (lib/ui/screens/library_screen.dart:231-522, spec 03 §4.3):
// 200 items per page sorted by updatedAt desc, up to 50 pages, a cursor of
// the newest `updatedAt ?? addedAt` seen, and a full resync every 24 hours.
// Unlike Flutter, a failed request aborts the sync without touching the
// cache, so going offline never wipes the library, and a full sync pages
// until the end (up to 500 pages) and prunes only when it got there.

/** Page cap of an incremental sync (Flutter). */
export const SYNC_MAX_PAGES = 50;
/** Page cap of a full sync (100,000 books): it must reach the end to prune. */
export const FULL_SYNC_MAX_PAGES = 500;
export const FULL_RESYNC_INTERVAL_MS = 24 * 60 * 60 * 1000;

export const syncCursorKey = (scope: string): string => `abs_books_sync_cursor_v1:${scope}`;
export const lastFullSyncKey = (scope: string): string => `abs_books_last_full_sync_v1:${scope}`;

export type LibrarySyncPhase = 'idle' | 'syncing' | 'done' | 'error';

export interface LibrarySyncState {
  phase: LibrarySyncPhase;
  /** `Unable to load books: <reason>` after a failed sync. */
  error: string | null;
  /** The failure itself (an `AbsError` when the server answered), so error views can show the mapped copy. */
  errorDetail: unknown;
  /** Epoch ms of the last successful sync. */
  lastSyncAt: number | null;
  /** The running (or last) sync was a full resync. */
  full: boolean;
  /**
   * The running sync is filling an empty cache (a first visit): its pages arrive
   * in `updatedAt` order, so a list sorted any other way would reorder as each
   * page lands (`useLibraryBooks` shows the server's newest books meanwhile).
   */
  initial: boolean;
}

export const librarySyncStore = createStore<Record<string, LibrarySyncState>>({});

const IDLE: LibrarySyncState = { phase: 'idle', error: null, errorDetail: null, lastSyncAt: null, full: false, initial: false };

function setSyncState(scope: string, patch: Partial<LibrarySyncState>): void {
  librarySyncStore.set((all) => ({ ...all, [scope]: { ...(all[scope] ?? IDLE), ...patch } }));
}

export function getLibrarySyncState(scope: string | null): LibrarySyncState {
  return (scope && librarySyncStore.get()[scope]) || IDLE;
}

export function useLibrarySyncState(scope: string | null): LibrarySyncState {
  return useStore(librarySyncStore, (all) => (scope && all[scope]) || IDLE);
}

/** `updatedAt ?? addedAt` (Flutter `Book.syncTimestamp`). */
export function syncTimestamp(book: Pick<BookSummary, 'updatedAt' | 'addedAt'>): number {
  return book.updatedAt ?? book.addedAt;
}

export function shouldRunFullSync(lastFullSyncAt: number | null | undefined, now = Date.now()): boolean {
  return !lastFullSyncAt || lastFullSyncAt <= 0 || now - lastFullSyncAt >= FULL_RESYNC_INTERVAL_MS;
}

/** True when every book's sync timestamp is ≤ the previous one (lists under 2 count as sorted). */
export function isSortedBySyncTimestampDesc(books: readonly Pick<BookSummary, 'updatedAt' | 'addedAt'>[]): boolean {
  for (let i = 1; i < books.length; i++) {
    if (syncTimestamp(books[i - 1] as BookSummary) < syncTimestamp(books[i] as BookSummary)) return false;
  }
  return true;
}

export interface SyncPageDecision {
  /** Books to write (newer than the cursor, or all when the cutoff is off). */
  persist: BookSummary[];
  /** The page contained a book at or below the cursor. */
  reachedCutoff: boolean;
  /** Whether the delta cutoff still applies after this page. */
  allowDeltaCutoff: boolean;
  /** Newest timestamp seen so far. */
  maxSeen: number | null;
}

/**
 * One page of the sync loop (pure, exported for tests): page-0 recovery when
 * the cache is tiny, sortedness check, de-duplication and the cursor cutoff.
 */
export function planSyncPage(
  page: number,
  books: readonly BookSummary[],
  state: { allowDeltaCutoff: boolean; cursor: number | null; cachedCountBefore: number; maxSeen: number | null },
): SyncPageDecision {
  let allowDeltaCutoff = state.allowDeltaCutoff;
  // A cursor that may only cover page 1 of an older partial seed.
  if (page === 0 && allowDeltaCutoff && state.cachedCountBefore > 0 && state.cachedCountBefore <= books.length) allowDeltaCutoff = false;
  if (allowDeltaCutoff && !isSortedBySyncTimestampDesc(books)) allowDeltaCutoff = false;
  const seen = new Set<string>();
  const persist: BookSummary[] = [];
  let reachedCutoff = false;
  let maxSeen = state.maxSeen;
  const cutoff = state.cursor ?? 0;
  for (const book of books) {
    if (seen.has(book.id)) continue;
    seen.add(book.id);
    const ts = syncTimestamp(book);
    if (ts > 0 && (maxSeen == null || ts > maxSeen)) maxSeen = ts;
    if (allowDeltaCutoff && ts <= cutoff) {
      reachedCutoff = true;
      continue;
    }
    persist.push(book);
  }
  return { persist, reachedCutoff, allowDeltaCutoff, maxSeen };
}

function readNumber(key: string): number | null {
  const v = storage.read<unknown>(key, null);
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function errorText(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return 'Unknown error';
}

async function finishedIdsFromServer(): Promise<Set<string>> {
  try {
    const user = await getMe();
    setProgressFromUser(user);
    return finishedBookIds(mediaProgressOf(user));
  } catch {
    return finishedBookIds(Object.values(progressStore.get()) as Array<MediaProgress & Record<string, unknown>>);
  }
}

function toCacheBook(item: LibraryItem, progress: Record<string, MediaProgress>, downloaded: Set<string>, finished: Set<string>): BookSummary {
  const book = bookFromLibraryItem(item, { progress: progress[item.id], isDownloaded: downloaded.has(item.id) });
  return finished.has(book.id) && !book.isFinished ? { ...book, isFinished: true, progress: 1 } : book;
}

const inFlight = new Map<string, Promise<void>>();

export interface SyncOptions {
  /** Force a full resync (prunes items deleted on the server). */
  full?: boolean;
}

/**
 * Syncs a library into the local cache. Concurrent calls for the same scope
 * share one run. Never rejects: failures land in `librarySyncStore`.
 */
export function syncLibrary(libraryId: string, opts: SyncOptions = {}): Promise<void> {
  const session = getSession();
  const scope = libraryScopeKey(libraryId, session);
  if (!session || !scope || !libraryId) return Promise.resolve();
  const running = inFlight.get(scope);
  if (running) return running;
  const run = runSync(libraryId, scope, session.serverUrl, session.userId, opts).finally(() => inFlight.delete(scope));
  inFlight.set(scope, run);
  return run;
}

async function runSync(libraryId: string, scope: string, serverUrl: string, userId: string, opts: SyncOptions): Promise<void> {
  const stillCurrent = () => {
    const s = getSession();
    return !!s && s.serverUrl === serverUrl && s.userId === userId;
  };
  const lastFull = readNumber(lastFullSyncKey(scope));
  const full = !!opts.full || shouldRunFullSync(lastFull);
  setSyncState(scope, { phase: 'syncing', error: null, errorDetail: null, full, initial: false });
  try {
    const [finished, cache] = await Promise.all([finishedIdsFromServer(), loadScope(scope)]);
    // Set before the first page is written, so no list ever shows a partial first sync.
    if (cache.size === 0) setSyncState(scope, { initial: true });
    const downloaded = downloadedIds(downloadsStore.get());
    const cursor = full ? null : readNumber(syncCursorKey(scope));
    const fullSyncBooks: BookSummary[] = [];
    let allowDeltaCutoff = cursor != null;
    let maxSeen: number | null = null;
    const cachedCountBefore = cache.size;
    // A full sync prunes the cache, so it must see every page: it ends on an
    // empty page; the page cap or a page with nothing new (a server ignoring
    // `page`) leaves it incomplete.
    let reachedEnd = false;
    const seenIds = new Set<string>();

    for (let page = 0; page < (full ? FULL_SYNC_MAX_PAGES : SYNC_MAX_PAGES); page++) {
      const data = await getLibraryItemsForSync(libraryId, page);
      if (!stillCurrent()) return;
      const items = (data.results ?? []).filter((i) => i && typeof i === 'object');
      if (items.length === 0) {
        reachedEnd = true;
        break;
      }
      const progress = progressStore.get();
      const books = items.map((i) => toCacheBook(i, progress, downloaded, finished));
      const seenBefore = seenIds.size;
      books.forEach((b) => seenIds.add(b.id));
      const decision = planSyncPage(page, books, { allowDeltaCutoff, cursor, cachedCountBefore, maxSeen });
      allowDeltaCutoff = decision.allowDeltaCutoff;
      maxSeen = decision.maxSeen;
      if (decision.persist.length > 0) {
        if (full) fullSyncBooks.push(...decision.persist);
        await upsertScopeBooks(scope, decision.persist);
      }
      if (allowDeltaCutoff && decision.reachedCutoff) break;
      if (seenIds.size === seenBefore) break;
      // Short pages are not treated as the last one: some servers clamp `limit`.
    }

    if (!stillCurrent()) return;
    const now = Date.now();
    if (full && reachedEnd) {
      await replaceScopeBooks(scope, fullSyncBooks);
      storage.write(lastFullSyncKey(scope), now);
      storage.write(syncCursorKey(scope), maxSeen ?? 0);
    } else if (full) {
      // Incomplete: rows were upserted, nothing is pruned, and the next run is a full sync again.
      if (maxSeen != null) storage.write(syncCursorKey(scope), maxSeen);
    } else if (maxSeen != null && (cursor == null || maxSeen > cursor)) {
      storage.write(syncCursorKey(scope), maxSeen);
    }
    setSyncState(scope, { phase: 'done', error: null, errorDetail: null, lastSyncAt: now, initial: false });
  } catch (err) {
    if (!stillCurrent()) return;
    setSyncState(scope, { phase: 'error', error: `Unable to load books: ${errorText(err)}`, errorDetail: err, initial: false });
  }
}
